import { useQuery } from "@rocicorp/zero/react"
import {
	IconDirectionsFill18 as AutomaticIcon,
	IconLifeRingFill18 as SupportIcon,
} from "nucleo-ui-fill-18"

import type { PortalLandingSection } from "@productlane/lib/src/portal-landing"
import type { IconType } from "@productlane/ui"
import type { HelpCenterTab } from "@productlane/zero"
import { isPortalLandingValue } from "@productlane/lib/src/portal-landing"
import { queries } from "@productlane/zero/queries"

import type {
	SettingsDropdownItem,
	SettingsDropdownOption,
} from "@/screens/settings/settings-dropdown"
import { tabIconComponent } from "@/screens/app/helpCenter/tab-icon"
import { ChangelogIcon, DocsIcon, RequestsIcon } from "@/screens/app/nav-icons"
import {
	findDropdownOption,
	SettingsDropdown,
} from "@/screens/settings/settings-dropdown"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"

const AUTOMATIC = "auto"

export const LANDING_COPY = {
	title: "Home page",
	description: "Where visitors go when they open the portal URL.",
	automatic: "Automatic",
	defaultDocsTab: "Documentation",
}

const SECTIONS: {
	section: PortalLandingSection
	label: string
	icon: IconType
}[] = [
	{ section: "docs", label: "Docs", icon: DocsIcon as IconType },
	{ section: "changelog", label: "Changelog", icon: ChangelogIcon as IconType },
	{
		section: "roadmap",
		label: "Feature requests",
		icon: RequestsIcon as IconType,
	},
	{ section: "support", label: "Support portal", icon: SupportIcon },
]

type LandingItem = SettingsDropdownItem
export type LandingEntry = SettingsDropdownOption

/** Only pages the portal serves: sections that are on and published tabs that belong to it. */
export function landingOptions(args: {
	sections: Record<PortalLandingSection, boolean>
	tabs: readonly Pick<
		HelpCenterTab,
		"id" | "name" | "icon" | "isPublished" | "portalInstanceId"
	>[]
	portalInstanceId: string | null
}): LandingEntry[] {
	const tabs = args.tabs
		.filter(
			(tab) =>
				tab.isPublished &&
				(tab.portalInstanceId ?? null) === args.portalInstanceId,
		)
		.map((tab) => ({
			value: `tab:${tab.id}`,
			label: tab.name,
			icon: tabIconComponent(tab.icon),
		}))

	const sections = SECTIONS.filter(({ section }) => args.sections[section]).map(
		({ section, label, icon }): LandingEntry => {
			const entry = { value: section, label, icon }
			if (section !== "docs" || !tabs.length) return entry
			const defaultTab = {
				value: section,
				label: LANDING_COPY.defaultDocsTab,
				icon: tabIconComponent("BookOpen"),
			}
			return { ...entry, children: [defaultTab, ...tabs] }
		},
	)

	return [
		{ value: AUTOMATIC, label: LANDING_COPY.automatic, icon: AutomaticIcon },
		...sections,
	]
}

/** A stored page the portal no longer serves shows as Automatic, which is where the root goes. */
export function selectedLanding(
	value: string | null | undefined,
	entries: readonly LandingEntry[],
): LandingItem {
	return findDropdownOption(value, entries) ?? entries[0]!
}

export function PortalLandingSelect({
	value,
	sections,
	portalInstanceId,
	disabled,
	onChange,
}: {
	value: string | null | undefined
	sections: Record<PortalLandingSection, boolean>
	portalInstanceId: string | null
	disabled?: boolean
	onChange: (landing: string | null) => void
}) {
	const [tabs] = useQuery(queries.helpCenterTabs())
	const entries = landingOptions({ sections, tabs, portalInstanceId })

	return (
		<SettingItem
			title={LANDING_COPY.title}
			description={LANDING_COPY.description}
			action={
				<SettingsDropdown
					value={selectedLanding(value, entries).value}
					options={entries}
					disabled={disabled}
					onChange={(next) =>
						onChange(
							next !== AUTOMATIC && isPortalLandingValue(next) ? next : null,
						)
					}
				/>
			}
		/>
	)
}
